import {
  AssetFillState,
  AssetStatus,
  enumAssetFillState,
  enumAssetStatus,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Badge,
  BadgeProps,
  Button,
  EntityDetails,
  Menu,
  ensureValue,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideTrash2 } from 'lucide-react';

import * as state from '../../../_state/assetDetail.state';
import { $deleteRemoteAsset } from '../../../_state/assetEditing.state';

import { AssetTabDetails } from './AssetTabDetails';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const getAssetStatusColor = (status: AssetStatus): BadgeProps['color'] => {
  switch (status) {
    case enumAssetStatus.Deployed:
      return 'olive';
    case enumAssetStatus.InTransit:
      return 'teal';
    case enumAssetStatus.Inventory:
      return 'purple';
    case enumAssetStatus.OutOfOrder:
      return 'failure';
    default:
      return 'neutral';
  }
};

const getFillStateColor = (fillState: AssetFillState): BadgeProps['color'] => {
  switch (fillState) {
    case enumAssetFillState.Full:
      return 'olive';
    case enumAssetFillState.Empty:
      return 'neutral';
    case enumAssetFillState.Unknown:
      return 'warning';
    default:
      return 'neutral';
  }
};

export const AssetDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();
  const [tab, setTab] = useAtom(state.$tab);
  const asset = useAtomValue(state.$selectedAsset);
  const deleteAsset = useSetAtom($deleteRemoteAsset);

  const handleBackPressed = () =>
    setLocation('/transportation/assets/list', { replace: true });

  const handleDeleteAsset = () => {
    deleteAsset({ id: ensureValue(asset?.id) });
    handleBackPressed();
  };

  const badges = asset
    ? [
        <Badge key="status" color={getAssetStatusColor(asset.status)}>
          {asset.status}
        </Badge>,
        <Badge key="fillState" color={getFillStateColor(asset.fillState)}>
          {asset.fillState}
        </Badge>,
      ]
    : undefined;

  return (
    <EntityDetails
      className="@AssetDetailView"
      primaryTitle={asset?.identifier ?? 'Loading...'}
      subtitle={asset?.type?.name}
      badges={badges}
      tabs={{
        details: {
          title: t.assetsView.detail.tabs.details,
          content: AssetTabDetails,
        },
      }}
      currentTab={tab}
      headerActions={
        <Menu
          trigger={
            <Button color="failure" variant="outline" icon={<LucideTrash2 />}>
              {t.assetsView.detail.buttons.delete}
            </Button>
          }
          items={[
            {
              label: t.assetsView.detail.buttons.confirmDelete,
              onAction: handleDeleteAsset,
            },
          ]}
        />
      }
      onBackPressed={handleBackPressed}
      onChangeTab={setTab}
    />
  );
};
